import { type ReactElement, type ReactNode, useState } from 'react';
import { Card } from './Card.js';
import { ErrorAlert } from './Alert.js';

export interface CollapsibleCardProps {
  readonly title: string;
  readonly subtitle?: ReactNode;
  /** Next to the title; stays visible while collapsed, since it acts on the body rather than showing it. */
  readonly actions?: ReactNode;
  readonly error?: unknown;
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
}

/**
 * A card whose body starts collapsed.
 *
 * A sojourn page with every section open at once is mostly scrolling past
 * things the organiser already set up; starting closed turns the page into a
 * table of contents first, a place to work second.
 */
export function CollapsibleCard({
  title,
  subtitle,
  actions,
  error,
  defaultOpen = false,
  children,
}: CollapsibleCardProps): ReactElement {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card>
      <div className="stack">
        <div className="row row--between collapsible-card__header">
          <button
            type="button"
            className="collapsible-card__toggle"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            <span className="collapsible-card__chevron" aria-hidden="true">
              {open ? '▾' : '▸'}
            </span>
            <span className="collapsible-card__title-row">
              <h2>{title}</h2>
              {subtitle !== undefined ? <span className="card__meta">{subtitle}</span> : null}
            </span>
          </button>
          {actions !== undefined ? <div className="row">{actions}</div> : null}
        </div>

        <ErrorAlert error={error} />

        {open ? children : null}
      </div>
    </Card>
  );
}
